import { DialogCloseEvent } from '@theme/dialog';
import { clamp } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.35;
const TAP_MOVE_THRESHOLD = 5;
const DISMISS_AXIS_THRESHOLD = 10;

/** @extends {Component<{ image: HTMLImageElement }>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #scale = MIN_ZOOM;
  #position = { x: 0, y: 0 };
  #pointers = new Map();
  #gesture = null;
  #lastTap = null;
  #pinched = false;
  #dismissOffset = 0;
  #resizeObserver = new ResizeObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('wheel', this.#onWheel, { passive: false });
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    window.addEventListener(DialogCloseEvent.eventName, this.reset);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('wheel', this.#onWheel);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    window.removeEventListener(DialogCloseEvent.eventName, this.reset);
  }

  #maxScale() {
    const image = this.refs.image;
    if (!image?.complete || !image.naturalWidth || !image.offsetWidth) return MAX_ZOOM;
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, image.naturalWidth / image.offsetWidth, image.naturalHeight / image.offsetHeight));
  }

  #limitPosition() {
    const image = this.refs.image;
    if (!image) return;
    const maxX = Math.abs(image.offsetWidth * this.#scale - this.clientWidth) / 2;
    const maxY = Math.abs(image.offsetHeight * this.#scale - this.clientHeight) / 2;
    this.#position.x = clamp(this.#position.x, -maxX, maxX);
    this.#position.y = clamp(this.#position.y, -maxY, maxY);
  }

  #render() {
    this.#scale = clamp(this.#scale, MIN_ZOOM, this.#maxScale());
    if (this.#scale === MIN_ZOOM) this.#position = { x: 0, y: 0 };
    this.#limitPosition();
    this.style.setProperty('--drag-zoom-scale', String(this.#scale));
    this.style.setProperty('--drag-zoom-translate-x', `${this.#position.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#position.y}px`);
    this.classList.toggle('is-zoomed', this.#scale > MIN_ZOOM);
  }

  #zoomTo(scale, clientX = this.getBoundingClientRect().left + this.clientWidth / 2, clientY = this.getBoundingClientRect().top + this.clientHeight / 2) {
    const next = clamp(scale, MIN_ZOOM, this.#maxScale());
    const ratio = next / this.#scale;
    const bounds = this.getBoundingClientRect();
    const imageBounds = this.refs.image?.getBoundingClientRect();
    const imageCenterX = imageBounds ? imageBounds.left + imageBounds.width / 2 - this.#position.x : bounds.left + bounds.width / 2;
    const imageCenterY = imageBounds ? imageBounds.top + imageBounds.height / 2 - this.#position.y : bounds.top + bounds.height / 2;
    const focusX = clientX - imageCenterX;
    const focusY = clientY - imageCenterY;
    this.#position.x = focusX - (focusX - this.#position.x) * ratio;
    this.#position.y = focusY - (focusY - this.#position.y) * ratio;
    this.#scale = next;
    this.#render();
  }

  zoomIn() { this.#zoomTo(this.#scale + ZOOM_STEP); }
  zoomOut() { this.#zoomTo(this.#scale - ZOOM_STEP); }

  reset = () => {
    this.#scale = MIN_ZOOM;
    this.#position = { x: 0, y: 0 };
    this.#pointers.clear();
    this.#gesture = null;
    this.#lastTap = null;
    this.#pinched = false;
    this.classList.remove('is-gesturing');
    this.#clearDismiss();
    this.#render();
  };

  #setDismiss(offset) {
    this.#dismissOffset = offset;
    this.style.setProperty('--viewer-dismiss-y', `${offset}px`);
    this.closest('zoom-dialog')?.previewDismiss?.(offset);
  }

  #clearDismiss() {
    if (this.#dismissOffset && this.closest('zoom-dialog')?.refs.dialog.open) {
      this.closest('zoom-dialog').previewDismiss?.(0);
    }
    this.#dismissOffset = 0;
    this.style.removeProperty('--viewer-dismiss-y');
  }

  #onWheel = (event) => {
    event.preventDefault();
    this.#zoomTo(this.#scale * Math.exp(-event.deltaY * 0.002), event.clientX, event.clientY);
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.pointerType !== 'mouse') event.preventDefault();
    this.setPointerCapture(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, onImage: event.target === this.refs.image });
    this.classList.add('is-gesturing');
    if (this.#pointers.size === 2) {
      this.#clearDismiss();
      this.#pinched = true;
      const [a, b] = [...this.#pointers.values()];
      this.#gesture = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: this.#scale };
    } else {
      this.#gesture = {
        x: event.clientX, y: event.clientY, position: { ...this.#position },
        moved: false, onImage: event.target === this.refs.image,
        axis: null, started: performance.now(),
      };
    }
  };

  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { ...this.#pointers.get(event.pointerId), x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 2 && this.#gesture?.distance) {
      const [a, b] = [...this.#pointers.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      this.#zoomTo(this.#scale * distance / this.#gesture.distance, (a.x + b.x) / 2, (a.y + b.y) / 2);
      this.#gesture.distance = distance;
      return;
    }
    if (this.#pointers.size !== 1 || !this.#gesture?.position) return;
    const dx = event.clientX - this.#gesture.x;
    const dy = event.clientY - this.#gesture.y;
    if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD) this.#gesture.moved = true;
    if (event.pointerType === 'touch' && this.#scale === MIN_ZOOM && this.#gesture.onImage && !this.#pinched) {
      if (!this.#gesture.axis && Math.hypot(dx, dy) > DISMISS_AXIS_THRESHOLD) {
        this.#gesture.axis = Math.abs(dy) > Math.abs(dx) * 1.2 ? 'vertical' : 'horizontal';
      }
      if (this.#gesture.axis === 'vertical') {
        this.#setDismiss(dy);
        return;
      }
    }
    if (this.#scale === MIN_ZOOM || !this.#gesture.moved) return;
    this.#position = { x: this.#gesture.position.x + dx, y: this.#gesture.position.y + dy };
    this.#render();
  };

  #onPointerUp = (event) => {
    const pointer = this.#pointers.get(event.pointerId);
    if (!pointer) return;
    const gesture = this.#gesture;
    if (event.pointerType === 'touch' && gesture?.axis === 'vertical' && this.#pointers.size === 1) {
      const dy = event.clientY - gesture.y;
      const duration = Math.max(1, performance.now() - gesture.started);
      const distance = Math.abs(dy);
      const farEnough = distance >= Math.min(120, window.innerHeight * 0.16);
      const fastEnough = distance >= 40 && distance / duration >= 0.65;
      if (event.type === 'pointerup' && (farEnough || fastEnough) && !this.#pinched) {
        this.closest('zoom-dialog')?.dismissFromSwipe?.(dy);
      } else {
        this.classList.remove('is-gesturing');
        this.#clearDismiss();
      }
    }
    const wasTap = this.#pointers.size === 1 && !this.#gesture?.moved && !this.#pinched &&
      pointer.onImage && document.elementFromPoint(event.clientX, event.clientY) === this.refs.image;
    this.#pointers.delete(event.pointerId);
    if (event.type === 'pointerup' && event.pointerType === 'mouse' && wasTap) {
      this.#zoomTo(this.#scale > MIN_ZOOM ? MIN_ZOOM : Math.min(2, this.#maxScale()), event.clientX, event.clientY);
    } else if (event.type === 'pointerup' && event.pointerType === 'touch' && wasTap) {
      const now = performance.now();
      if (this.#lastTap && now - this.#lastTap.time < 300 && Math.hypot(event.clientX - this.#lastTap.x, event.clientY - this.#lastTap.y) < 40) {
        this.#zoomTo(this.#scale > MIN_ZOOM ? MIN_ZOOM : Math.min(2, this.#maxScale()), event.clientX, event.clientY);
        this.#lastTap = null;
      } else {
        this.#lastTap = { time: now, x: event.clientX, y: event.clientY };
      }
    } else if (event.pointerType === 'touch') {
      this.#lastTap = null;
    }
    if (this.#pointers.size === 1) {
      const [point] = [...this.#pointers.values()];
      this.#gesture = { x: point.x, y: point.y, position: { ...this.#position }, moved: false };
    } else if (!this.#pointers.size) {
      this.#gesture = null;
      this.#pinched = false;
      this.classList.remove('is-gesturing');
    }
  };
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}
